import { ErrorHandler, Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { handleChunkLoadError, isChunkLoadError } from "./chunk-error-handler";

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private isHandlingError = false;

  private sentryHandler = Sentry.createErrorHandler({
    showDialog: false,
    extractor(error: unknown, defaultExtractor: (error: unknown) => unknown): unknown {
      const defaultResult = defaultExtractor(error);
      if (defaultResult) {
        return defaultResult;
      }
      if (error === null || error === undefined) {
        return new Error("Unhandled HTTP error (empty response body)");
      }
      if (typeof error === "object") {
        const obj = error as Record<string, unknown>;
        const message = obj["detail"] || obj["message"] || obj["error"] || JSON.stringify(error);
        return new Error(`Unhandled HTTP error: ${message}`);
      }
      return new Error(`Unhandled error: ${String(error)}`);
    },
  });

  handleError(error: any): void {
    if (isChunkLoadError(error)) {
      void handleChunkLoadError();
      return;
    }

    // Ne pas reporter les erreurs réseau à Sentry (pas un bug applicatif)
    const errorMessage = error?.message || error?.rejection?.message || "";
    if (error?.name === "NetworkError" || errorMessage.includes("Erreur réseau")) {
      console.warn("[EVA] Erreur réseau ignorée par Sentry:", errorMessage);
      return;
    }

    // Bug interne PrimeNG TreeTable restoreFocus — row peut être undefined si composant
    // détruit ou index hors-bornes entre planification du setTimeout et son exécution (Sentry #6900348417)
    if (errorMessage.includes("isSameNode")) {
      console.warn("[EVA] PrimeNG TreeTable restoreFocus bug ignoré:", errorMessage);
      return;
    }

    // Stack overflow Angular (cdr.detectChanges récursif) — pas un bug applicatif à remonter (Sentry #7285360872)
    if (errorMessage.includes("Maximum call stack size exceeded")) {
      console.error("[EVA] Stack overflow Angular (change detection) — ignoré par Sentry (Sentry #7285360872):", error);
      return;
    }

    // Erreurs serveur backend (5xx) — déjà notifiées à l'utilisateur via toast,
    // pas un bug frontend à remonter dans Sentry (Sentry #7276402505)
    if (error?.name === "ServerError" || errorMessage.includes("Server error")) {
      console.warn("[EVA] Erreur serveur backend (ignorée par Sentry):", errorMessage);
      return;
    }

    // Erreurs métier backend (4xx) avec error_type connu — déjà gérées par les composants,
    // ne pas remonter dans Sentry
    const knownBusinessErrors = ["INTERVENTION_NOT_REALIZED", "OFFER_NOT_UP_TO_DATE"];
    if (typeof error === "object" && error !== null && knownBusinessErrors.includes(error?.error_type)) {
      console.warn("[EVA] Erreur métier ignorée par Sentry:", error?.error_type);
      return;
    }

    if (this.isHandlingError) {
      console.error("[EVA] Erreur récursive dans handleError (ignorée):", error?.message);
      return;
    }
    this.isHandlingError = true;
    try {
      this.sentryHandler.handleError(error);
    } catch (sentryError) {
      console.error("[EVA] Sentry internal error (ignored):", sentryError);
    } finally {
      this.isHandlingError = false;
    }
  }
}
